/**
 * Manager class for dealing with user settings.
 *
 *
 * Suggested format for user settings used by a view [view].[property on view model]. For example, DashboardViewerOptions.selectedDashboard
 */
import topic from "common/managers/topic";
import Message from "common/enums/main/Message";
import loginProps from "common/managers/loginProps";
import userOperations from "common/datasources/user/userOperations";


class UserSettingsManager {
    constructor(props) {
        this.server = new Map();
        this.local = new Map();
        this.triggers = new Set();
        (props || []).forEach(prop => {
            this.server.set(prop.key, prop.value);
        })
    }

    /**
     * Return the current value for a a given key.
     * @param key The key to search for
     * @param def The default value to return.  Defaults to undefined
     */
    get(key, def) {
        if(this.local.has(key)) {
            return this.local.get(key);
        } else if(this.server.has(key)) {
            return this.server.get(key);
        } else {
            return def;
        }
    }

    /**
     * Get the current value for a setting, as a boolean.
     *
     * @param key the setting name
     * @param def a default value to return if the setting does not exist.  Defaults to undefined.
     * @return {boolean}
     */
    getBoolean(key, def) {
        const val = this.get(key, def);

        // if no default provided, and no setting exists, default to false
        if (val !== undefined) {
            if (typeof(val) === "string" || val instanceof String) {
                return val === "true";
            } else {
                return !!val;
            }
        }
        return val;
    }

    /**
     * Get the current value for a setting as an integer.
     *
     * @param key setting key
     * @param def default value if setting does not exist
     * @return {*}
     */
    getInt(key, def) {
        let val = this.get(key, def);
        if (val !== undefined) {
            val = parseInt(val);

            if(isNaN(val)) {
                val = def;
            }
        }
        return val;
    }

    /**
     * Set a value for a given key
     * @param key Key to set value for
     * @param value the new value
     * @param sync whether to sync changes immediately.  If false, waits to sync.  If true or undefined, syncs immediately.
     * @param trigger whether to trigger an event when the setting is synced
     */
    set(key, value, sync, trigger) {
        if(sync !== false) {
            return this.setNow(key, value, trigger);
        } else {
            this.local.set(key, value);
            if(trigger) {
                this.triggers.add(key);
            }
            return $.when();
        }
    }

    setNow(key, value, trigger) {
        this.local.set(key, value);
        return userOperations.setUserSetting(key, value)
            .done(() => {
                if(trigger) {
                    topic.publish(Message.USER_SETTING_CHANGED, [key]);
                }
                this.server.set(key, value);
                this.local.delete(key);
                this.triggers.delete(key);
            });
    }

    sync() {
        const promises = [];
        this.local.forEach((value, key) => {
            promises.push(this.setNow(key, value, this.triggers.has(key)));
        });
        return $.when.apply($, promises);
    }

    cancelChanges() {
        return this.local = new Map();
    }

    _map(f) {
        const res = [];
        const keys = new Set();
        this.local.forEach((value, key) => {
            keys.add(key);
            res.push(f(value, key));
        });
        this.server.forEach((value, key) => {
            if(!keys.has(key)) {
                res.push(f(value, key));
            }
        });
        return res;
    }

    filter(includes) {
        return this._map((value, key) => ({settingValue: value, settingName: key}))
            .filter(prop => prop.settingName.includes(includes));
    }
}

export default new UserSettingsManager(loginProps.get().userSettings);
